import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { submitFeedback } from './submitFeedback.ts';
import { useAppStore } from '../../../../../GlobalStore/GlobalStore.ts';

interface FeedbackProps {}

const Feedback: React.FC<FeedbackProps> = () => {
    const [feedback, setFeedback] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const userObj = useAppStore((state: any) => state.userObj);
    const { user } = userObj;

    const handleSubmit = async () => {
        if (!feedback.trim()) return;

        setIsSubmitting(true);
        setError('');

        try {
            const result = await submitFeedback(user, feedback.trim());

            if (result.success) {
                setSubmitted(true);
                setFeedback('');
            } else {
                setError(result.error || 'Failed to submit feedback. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
            setError('An unexpected error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="dark:text-neutral-300 space-y-6">
            <div>
                <h3 className="text-lg font-medium dark:text-neutral-100 mb-2">We'd Love Your Feedback</h3>
                <p className="text-sm dark:text-neutral-400">
                    Your feedback helps us improve Onuro. Let us know what you think about the app, features you'd like to see, or any issues you've encountered.
                </p>
            </div>

            {submitted ? (
                <div className="bg-green-100 dark:bg-green-900/30 p-4 rounded-lg">
                    <p className="text-green-800 dark:text-green-400 font-medium">Thank you for your feedback!</p>
                    <p className="text-sm text-green-700 dark:text-green-500 mt-1">We appreciate your input and will use it to improve Onuro.</p>
                    <button
                        className="mt-3 text-sm font-medium text-green-800 dark:text-green-400 hover:underline"
                        onClick={() => setSubmitted(false)}
                    >
                        Submit another feedback
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        <label htmlFor="feedback" className="block text-sm font-medium mb-1 dark:text-neutral-300">
                            Your Feedback
                        </label>
                        <textarea
                            id="feedback"
                            rows={5}
                            className="w-full px-3 py-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:outline-none transition-all duration-200 resize-none"
                            placeholder="Share your thoughts, suggestions, or report issues..."
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                        />
                    </div>

                    {error && (
                        <div className="text-red-600 dark:text-red-400 text-sm">
                            {error}
                        </div>
                    )}

                    <div className="flex justify-center">
                        <button
                            className={`py-2 px-4 rounded-md flex items-center space-x-2 transition-all duration-200 cursor-pointer ${isSubmitting ? 'bg-cyan-700 cursor-not-allowed' : 'bg-cyan-600 hover:bg-cyan-700'}`}
                            onClick={handleSubmit}
                            disabled={isSubmitting || !feedback.trim()}
                        >
                            <span className="text-white font-medium">
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                            </span>
                            <FontAwesomeIcon icon={faPaperPlane} className="text-white h-4 w-4 ml-2" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Feedback;